import { useState } from "react";
import { FaLock, FaPhoneAlt, FaShieldAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";

function getCurrentDevCode(date = new Date()) {
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${day}${hour}${minute}`;
}

function DevModulePage() {
  const [hasAccess, setHasAccess] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  usePageTitle("Developer Module | Pargro");

  const handleCodeChange = (event) => {
    setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
    setError("");
  };

  const handleVerify = (event) => {
    event.preventDefault();

    if (!/^\d{6}$/.test(code)) {
      setError("Enter a valid 6-digit authentication code.");
      return;
    }

    if (code !== getCurrentDevCode()) {
      setError("Invalid authentication code.");
      return;
    }

    setCode("");
    setError("");
    setHasAccess(true);
  };

  const handleLock = () => {
    setCode("");
    setError("");
    setHasAccess(false);
  };

  if (hasAccess) {
    return (
      <section className="w-full px-4 py-10">
        <div className="rounded-lg bg-white p-7 shadow-[0_12px_30px_rgba(15,35,58,0.12)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mb-3 flex items-center gap-3">
                <FaShieldAlt className="h-7 w-7 text-[#1773f8]" aria-hidden="true" />
                <h1 className="m-0 text-2xl font-bold text-[#111827]">
                  Dev Module
                </h1>
              </div>
              <p className="m-0 text-[#4b5563]">
                Your private developer workspace is ready.
              </p>
            </div>
            <button
              className="inline-flex h-10 items-center gap-2 rounded-md border border-[#b42318] bg-white px-4 text-sm font-semibold text-[#b42318] transition hover:bg-[#fff1f0]"
              type="button"
              onClick={handleLock}
            >
              <FaLock aria-hidden="true" />
              Lock Dev Module
            </button>
          </div>
          <div className="mt-8 grid w-full gap-4">
            <article className="rounded-lg border border-[#dbe7f5] bg-[#f8fbff] p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <FaPhoneAlt className="mb-4 h-7 w-7 text-[#1773f8]" aria-hidden="true" />
              <h2 className="mb-2 text-lg font-bold text-[#062f58]">Sarvam Voice Automation</h2>
              <p className="mb-5 text-sm leading-6 text-[#4b5563]">Operate voice automation plans, runs, campaigns, and result recovery.</p>
              <Link className="inline-flex rounded-md bg-[#1773f8] px-4 py-2 text-sm font-semibold text-white no-underline hover:bg-[#0d63dc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1773f8]" to="/dev-module/sarvam">
                Open Module
              </Link>
            </article>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="grid min-h-[calc(100vh-74px)] place-items-center px-4 py-10">
      <form
        className="w-full max-w-md rounded-lg bg-white px-7 py-8 shadow-[0_12px_30px_rgba(15,35,58,0.12)]"
        onSubmit={handleVerify}
      >
        <FaLock className="mx-auto mb-4 h-8 w-8 text-[#073d70]" aria-hidden="true" />
        <h1 className="mb-3 text-center text-2xl font-bold text-[#111827]">
          Developer Access Required
        </h1>
        <p className="mb-6 text-center text-sm text-[#4b5563]">
          Enter the 6-digit developer authentication code.
        </p>
        <label
          className="mb-2 block text-sm font-semibold text-[#111827]"
          htmlFor="dev-module-code"
        >
          Authentication Code
        </label>
        <input
          className="h-12 w-full rounded-md border border-[#d8dee6] px-4 text-center text-xl tracking-[0.45em] outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.14)]"
          id="dev-module-code"
          name="code"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={handleCodeChange}
          autoFocus
        />
        {error ? (
          <p className="mb-0 mt-3 text-sm text-[#b42318]" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-md border-0 bg-[#1773f8] text-sm font-semibold text-white transition hover:bg-[#0d63dc] disabled:cursor-not-allowed disabled:bg-[#8dbefb]"
          type="submit"
          disabled={code.length !== 6}
        >
          Verify
        </button>
      </form>
    </section>
  );
}

export default DevModulePage;
